import { motion } from 'motion/react'
import {
  Sun,
  Calendar,
  Mail,
  BellRing,
  Newspaper,
  Briefcase,
  Users,
  ClipboardCheck,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BriefingData, BriefingAction, BriefingItemState } from '../../../shared/types/briefing'
import { useChatActions } from '../ChatContext'

const STATE_TONE: Record<BriefingItemState, { dot: string; chip: string }> = {
  pending:   { dot: 'bg-pearl/30',     chip: 'text-pearl/40' },
  approved:  { dot: 'bg-emerald-400/70', chip: 'text-emerald-400/70' },
  resolved:  { dot: 'bg-emerald-400/70', chip: 'text-emerald-400/70' },
  dismissed: { dot: 'bg-pearl/40',     chip: 'text-pearl/45' },
  denied:    { dot: 'bg-rose-400/60',    chip: 'text-rose-400/60' },
  expired:   { dot: 'bg-pearl/30',     chip: 'text-pearl/30' },
}

const ICON_MAP: Record<string, LucideIcon> = {
  calendar: Calendar,
  mail: Mail,
  'bell-ring': BellRing,
  newspaper: Newspaper,
  briefcase: Briefcase,
  users: Users,
  'clipboard-check': ClipboardCheck,
  'trending-up': TrendingUp,
}

const isTimeLabel = (label: string) => /^\d{1,2}:\d{2}$/.test(label)
const isEmojiLabel = (label: string) => {
  const trimmed = label.trim()
  return trimmed.length > 0 && trimmed.length <= 8 && !/[\p{L}\p{N}]/u.test(trimmed)
}

interface Props {
  briefing: BriefingData
  /** Required when the card is rendered inside a chat message — needed by typed
   *  actions (dismiss / resolve_followup) to mutate the right message locally.
   *  Optional only for compatibility with non-chat preview surfaces. */
  messageId?: string
}

export default function BriefingCard({ briefing, messageId }: Props) {
  const { t } = useTranslation('chat')
  const { onSendMessage, onDismissBriefingItem, onResolveFollowup, onOpenEmail } = useChatActions()

  const dispatchAction = (action: BriefingAction, sectionIndex: number, itemIndex: number) => {
    if (action.kind === 'dismiss_briefing') {
      if (messageId) {
        onDismissBriefingItem(messageId, {
          description: action.description,
          itemKey: action.itemKey,
          sectionIndex,
          itemIndex,
        })
      }
      return
    }
    if (action.kind === 'resolve_followup') {
      if (messageId) {
        onResolveFollowup(messageId, {
          followupId: action.followupId,
          sectionIndex,
          itemIndex,
        })
      }
      return
    }
    if (action.kind === 'approval_ref') {
      // Phase B-future: tap opens the approval flow inline. Until materialization
      // lands, no action originates with this kind, so this branch is a no-op
      // gracefully rather than throwing.
      return
    }
    // Default: legacy intent action — type the sentence into chat.
    onSendMessage(action.intent)
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="glass-card rounded-2xl overflow-hidden border border-gold/20"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-gold/10 to-gold/5 border-b border-gold/10">
        <Sun className="w-5 h-5 text-gold/60 shrink-0" />
        <div>
          <div className="font-display text-lg text-ivory font-medium">{briefing.greeting}</div>
          <div className="text-pearl/50 text-xs">{briefing.date}</div>
        </div>
      </div>

      {/* Sections */}
      {briefing.sections.map((section, i) => {
        const Icon = section.icon ? ICON_MAP[section.icon] : undefined

        return (
          <div key={i} className="px-5 py-4 border-b border-white/5">
            {/* Section header */}
            <div className="flex items-center gap-2 mb-3">
              {Icon && <Icon className="w-3.5 h-3.5 text-gold/50" />}
              <span className="text-[11px] text-gold uppercase tracking-wider font-medium">
                {section.title}
              </span>
            </div>

            {/* Items */}
            <div className="space-y-3">
              {section.items.map((item, j) => {
                const isResolved = item.state && item.state !== 'pending'
                const tone = isResolved && item.state ? STATE_TONE[item.state] : null
                const tappableEmail = !isResolved && !!item.emailId

                const rowContent = isTimeLabel(item.label) ? (
                  /* Calendar-style: time pill inline with value */
                  <div className={`flex items-start gap-2.5 ${isResolved ? 'line-through decoration-pearl/30' : ''}`}>
                    <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded bg-white/5 text-[11px] font-mono text-pearl/60">
                      {item.label}
                    </span>
                    <span className="text-sm text-ivory leading-snug">{item.value}</span>
                  </div>
                ) : isEmojiLabel(item.label) ? (
                  /* Techpresso-style: emoji inline with one-sentence headline */
                  <div className={`flex items-start gap-2.5 ${isResolved ? 'line-through decoration-pearl/30' : ''}`}>
                    <span className="shrink-0 text-base leading-snug">{item.label}</span>
                    <span className="text-sm text-ivory leading-snug">{item.value}</span>
                  </div>
                ) : (
                  /* Stacked: label above value */
                  <div className={isResolved ? 'line-through decoration-pearl/30' : ''}>
                    <div className="text-[11px] text-pearl/70 font-medium mb-0.5">
                      {item.label}
                    </div>
                    <div className="text-sm text-ivory leading-snug">{item.value}</div>
                  </div>
                )

                return (
                  <div key={j} className={isResolved ? 'opacity-50 transition-opacity' : ''}>
                    {tappableEmail ? (
                      <button
                        type="button"
                        onClick={() => onOpenEmail(item.emailId!)}
                        className="w-full text-left -mx-2 px-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors"
                      >
                        {rowContent}
                      </button>
                    ) : rowContent}

                    {/* Status chip — shown in place of action pills once the item is resolved */}
                    {isResolved && tone ? (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                        <span className={`text-[10.5px] uppercase tracking-wider ${tone.chip}`}>
                          {t(`briefingCard.state.${item.state ?? 'resolved'}`, item.state ?? '')}
                        </span>
                      </div>
                    ) : (
                      item.actions && item.actions.length > 0 && (
                        <div className="flex gap-1.5 mt-1.5">
                          {item.actions.map((action: BriefingAction) => (
                            <button
                              key={action.id}
                              onClick={() => dispatchAction(action, i, j)}
                              className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                action.variant === 'destructive'
                                  ? 'border border-rose-400/25 text-rose-400/70 hover:bg-rose-400/10'
                                  : 'border border-gold/20 text-gold/70 hover:bg-gold/10'
                              }`}
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Footer */}
      <div className="px-5 py-3 bg-gold/5 text-center">
        <span className="font-display italic text-sm text-ivory">{briefing.signoff}</span>
      </div>
    </motion.div>
  )
}
